import { Link } from "react-router-dom";
import { ArrowRight, Facebook, Instagram, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { EMAIL, NAV_LINKS, PHONE_DISPLAY, PHONE_TEL, SERVICES } from "@/lib/site";
import { Reveal } from "./Reveal";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-line bg-ink">
      <div className="blueprint-grid-fine border-b border-line">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 py-16 sm:px-8 sm:py-20 lg:flex-row lg:items-center lg:px-16">
          <Reveal>
            <h2 className="max-w-xl font-display text-3xl font-black uppercase leading-[1.02] tracking-tight sm:text-4xl">
              Have a project in mind? Let's walk the property.
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-fog">
              Bring us the idea — even if you are unsure about cost, financing, permits, plans, or where to begin.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="flex flex-col gap-3 sm:flex-row">
            <Link to="/contact" data-testid="footer-cta-consultation-button" className="btn-primary">
              Request a Project Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={PHONE_TEL} data-testid="footer-call-button" className="btn-outline">
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-8 md:grid-cols-2 lg:grid-cols-4 lg:px-16">
        <div>
          <img src="/logo-white.png" alt="Black Sky Builders" className="h-16 w-auto" />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-fog">
            A Salt Lake City construction company guiding residential and commercial projects from early planning
            through construction and completion.
          </p>
          <div className="mt-6 flex gap-3">
            {[
              { icon: Facebook, label: "Facebook", testid: "social-facebook-link" },
              { icon: Instagram, label: "Instagram", testid: "social-instagram-link" },
              { icon: Linkedin, label: "LinkedIn", testid: "social-linkedin-link" },
            ].map(({ icon: Icon, label, testid }) => (
              <a
                key={label}
                href="#"
                aria-label={`${label} (coming soon)`}
                data-testid={testid}
                className="flex h-10 w-10 items-center justify-center border border-line text-fog transition-colors hover:border-silver hover:text-white"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-steel">Company</h3>
          <ul className="mt-5 space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`footer-${l.testid}`} className="text-sm text-fog transition-colors hover:text-white">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-steel">What We Build</h3>
          <ul className="mt-5 space-y-3">
            {SERVICES.map((s) => (
              <li key={s.num}>
                <Link to={s.link} data-testid={`footer-service-${s.num}`} className="text-sm text-fog transition-colors hover:text-white">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-steel">Contact</h3>
          <ul className="mt-5 space-y-4 text-sm text-fog">
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-steel" />
              <span>Salt Lake City, Utah<br />Serving communities throughout Utah</span>
            </li>
            <li>
              <a href={PHONE_TEL} data-testid="footer-phone-link" className="flex items-center gap-3 transition-colors hover:text-white">
                <Phone className="h-4 w-4 shrink-0 text-steel" /> {PHONE_DISPLAY}
              </a>
            </li>
            <li>
              <a href={`mailto:${EMAIL}`} data-testid="footer-email-link" className="flex items-center gap-3 break-all transition-colors hover:text-white">
                <Mail className="h-4 w-4 shrink-0 text-steel" /> {EMAIL}
              </a>
            </li>
            <li className="border-t border-line pt-4">
              <p data-testid="footer-license-line" className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-steel">
                Black Sky Builders LLC | Utah B100 General Building Contractor | License #14298062-5501
              </p>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-line">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-6 font-mono text-[11px] uppercase tracking-[0.15em] text-steel sm:px-8 md:flex-row md:items-center md:justify-between lg:px-16">
          <span>© 2026 Black Sky Builders — Salt Lake City, Utah</span>
          <div className="flex flex-wrap items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-silver">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-silver">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
